import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';

import { AuthService } from './auth.service';
import { sessionExpiredLoginUrl } from './session-redirect';

/**
 * Allow the route only if the user already has an Auth0 session. If they
 * don't, send them to `/login` (the marketing intro). The intro screen owns
 * the actual `loginWithRedirect()` call so users see the splash page first
 * and click through to Auth0 themselves — same flow as the legacy app.
 *
 * This guard only proves a session exists. Requiring a *determined account* is
 * a separate concern handled by `accountGuard`, composed onto the routes that
 * need one (see account.guard.ts).
 */
export const authGuard: CanActivateFn = async (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.isAuthenticated()) {
    return true;
  }

  switch (await auth.restoreSession()) {
    case 'authenticated':
      return true;
    case 'expired':
      return router.parseUrl(sessionExpiredLoginUrl(state.url));
    case 'signed-out':
      return router.parseUrl(buildLoginUrl(state.url));
  }
};

function buildLoginUrl(targetUrl: string): string {
  if (!targetUrl || targetUrl === '/' || targetUrl.startsWith('/login')) {
    return '/login';
  }
  return `/login?redirect_url=${encodeURIComponent(targetUrl)}`;
}
